import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Box, Input, Button, Text, useOutsideClick } from '@chakra-ui/react';
import { FixedSizeList } from 'react-window';

type SearchableDropdownProps = {
    value: string | undefined;
    items: string[];
    placeholder: string;
    onSelect: (value?: string) => void;
    isDisabled?: boolean;
    titleTag?: string;
};

export const SearchableDropdown = ({
    value,
    items,
    placeholder,
    onSelect,
    isDisabled,
    titleTag,
}: SearchableDropdownProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [listHeight, setListHeight] = useState(200);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useOutsideClick({
        ref: dropdownRef,
        handler: () => setIsOpen(false),
    });

    const calculateListHeight = useCallback(() => {
        if (buttonRef.current) {
            const buttonRect = buttonRef.current.getBoundingClientRect();
            const availableHeight = window.innerHeight - buttonRect.bottom - 20;
            setListHeight(Math.min(availableHeight, 300));
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            calculateListHeight();
            window.addEventListener('resize', calculateListHeight);
        }
        return () => {
            window.removeEventListener('resize', calculateListHeight);
        };
    }, [isOpen, calculateListHeight]);

    const filteredItems = useMemo(
        () => items.filter((item) => item.toLowerCase().includes(searchTerm.toLowerCase())),
        [searchTerm, items]
    );

    const onSelectItem = (value?: string) => {
        onSelect(value);
        if (value) {
            setIsOpen(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    return (
        <Box title={titleTag ?? ''} position="relative" width="100%">
            <Button
                ref={buttonRef}
                isDisabled={isDisabled}
                width="100%"
                textAlign="left"
                px={4}
                py={2}
                borderWidth="1px"
                borderRadius="md"
                bg="gray.50"
                _hover={{ bg: 'gray.100' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                {value || placeholder}
            </Button>
            {isOpen && (
                <Box
                    ref={dropdownRef}
                    position="absolute"
                    width="100%"
                    bg="white"
                    border="1px solid gray"
                    borderRadius="md"
                    zIndex={10}
                    boxShadow="lg"
                    overflow="hidden"
                    mt={1}
                >
                    <Box px={4} py={2} bg="gray.50">
                        <Input
                            isDisabled={isDisabled}
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleInputChange}
                            mb={2}
                            autoFocus={true}
                            ref={searchInputRef}
                            bg="white"
                            borderWidth="1px"
                            _focus={{ borderColor: 'blue.400', boxShadow: 'outline' }}
                        />
                    </Box>
                    {value && (
                        <Box
                            px={4}
                            py={2}
                            bg="danger.100"
                            color="danger.600"
                            cursor="pointer"
                            onClick={() => onSelectItem(undefined)}
                            _hover={{ bg: 'danger.200' }}
                        >
                            Clear selection
                        </Box>
                    )}
                    <FixedSizeList
                        height={listHeight}
                        itemCount={filteredItems.length}
                        itemSize={45}
                        width="100%"
                    >
                        {({ index, style }) => (
                            <Box
                                key={index}
                                style={style}
                                px={4}
                                py={2}
                                cursor="pointer"
                                bg="white"
                                _hover={{ bg: 'primary.50' }}
                                borderBottom="1px solid #eaeaea"
                                onClick={() => onSelectItem(filteredItems[index])}
                            >
                                <Text noOfLines={1} color="gray.800">
                                    {filteredItems[index]}
                                </Text>
                            </Box>
                        )}
                    </FixedSizeList>
                </Box>
            )}
        </Box>
    );
};
